import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogContentTextProps,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  TextFieldProps,
} from '@mui/material';

import { Close as CloseIcon } from '@mui/icons-material';
import { TP_COLORS } from '../../../../../constants';
import {
  IHomework,
  THomeworkFile,
} from '../../../../../store/V2/HomeworksSlice';
import { TPTextField } from '../../../../../components/Inputs';
import UploadHomeworkFiles from '../../admin/modals/ClassListOptionsModal/components/UploadHomeworkFiles';

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const dialogContentTextProps: DialogContentTextProps = {
  color: TP_COLORS.gray.light,
  fontSize: '14px',
  marginBottom: '50px',
};

const defaultButtonStyles = {
  padding: { xs: '6px 12px', sm: '12px 24px' },
  fontWeight: '600',
  borderRadius: '5px',
};

type TState = {
  description: string;
  files: THomeworkFile[];
};

const initialState: TState = {
  description: '',
  files: [],
};

interface AddFileModalProps {
  open: boolean;
  homework?: IHomework;
  onClose: () => void;
  onSubmit: (values) => void;
}

const AddFileModal: FunctionComponent<AddFileModalProps> = ({
  open,
  homework,
  onClose,
  onSubmit,
}) => {
  // State
  const [state, setState] = useState({ ...initialState });
  const [isEdit, setIsEdit] = useState(false);

  const isSubmitDisabled =
    !state.description.trim().length || !state.files.length;

  useEffect(() => {
    if (homework) {
      setIsEdit(true);
      setState({
        description: homework.description,
        files: homework.files || [],
      });
    }
  }, []);

  const handleFinishedUpload = async (files: THomeworkFile[]) => {
    setState((prevState) => ({
      ...prevState,
      files,
    }));
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px', maxWidth: '540px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px 30px 20px 30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        {isEdit ? 'Update' : 'Upload'} File
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <DialogContentText {...dialogContentTextProps}>
          Fill in the details and drag and drop your files to upload your
          homework. Stay organized and on track!
        </DialogContentText>
        <div style={{ paddingTop: '6px' }}>
          <Grid container spacing="20px">
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  name="description"
                  label="Description *"
                  placeholder="Description"
                  value={state.description}
                  onChange={(event) =>
                    setState((prevState) => ({
                      ...prevState,
                      description: event.target.value,
                    }))
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <UploadHomeworkFiles
                prevFiles={state.files}
                onFinish={handleFinishedUpload}
              />
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
        <Button
          disableElevation
          variant="contained"
          color="inherit"
          onClick={onClose}
          sx={{
            ...defaultButtonStyles,
            color: TP_COLORS.gray.darken,
            backgroundColor: TP_COLORS.border.color1,
          }}
        >
          Back
        </Button>
        <Button
          disableElevation
          variant="contained"
          color="secondary"
          onClick={() => {
            onSubmit(state);
            onClose();
          }}
          disabled={isSubmitDisabled}
          sx={{
            ...defaultButtonStyles,
            color: '#F8FAFC',
            backgroundColor: TP_COLORS.gray.darken,
          }}
        >
          {isEdit ? 'Update' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddFileModal;
